import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiPaperclip, FiSearch, FiX } from "react-icons/fi";
import { showErrorToast, showSuccessToast } from "../../constants/ToastService";
import { qualityApi } from "./qualityApi";
import { Modal, SEVERITY, SOURCES, SourceChip } from "./qualityUi";
import useQualityMeta, { orgsFor, subModulesOf } from "./useQualityMeta";

/**
 * Log a production bug.
 *
 * Four parts, matching what tracing and root cause need later:
 *   What happened  — summary, expected vs actual, steps, severity, when
 *   Where          — server, hospital, module, screen, version, URL, browser
 *   Impact         — users / hospitals affected, patient safety
 *   Evidence       — screenshots, logs, traces
 *
 * When the source is Customer Support or Error Tracking, a search box finds
 * the real record and copies its details in, so the bug is linked to
 * something that exists. Other screens can open this with `prefill`.
 */

const MAX_FILE_MB = 15;

// <input type="datetime-local"> wants local "YYYY-MM-DDTHH:mm".
const toLocalInput = (value) => {
    const d = value ? new Date(value) : new Date();
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EMPTY = {
    source: "internal", source_ref: "", title: "", severity: "P2", patient_safety: false,
    occurred_at: "", hospital_reporter: "",
    server_code: "", org_name: "", app_version: "", module: "", sub_module: "", screen_url: "", browser_device: "",
    expected_result: "", actual_result: "", steps_to_reproduce: "", details: "",
    users_affected: "", hospitals_affected: "",
};

export default function RaiseBugModal({ prefill = {}, onClose, onCreated }) {
    const meta = useQualityMeta();
    const [f, setF] = useState(() => ({ ...EMPTY, ...prefill, occurred_at: toLocalInput(prefill.occurred_at) }));
    const [linked, setLinked] = useState(prefill.source_ref ? { ref: prefill.source_ref, title: prefill.title } : null);
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState([]);
    const [saving, setSaving] = useState(false);
    const versionTouched = useRef(!!prefill.app_version);
    const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

    const searchable = f.source === "customer_support" || f.source === "error_tracking";
    const orgs = orgsFor(meta, f.server_code);
    const subs = subModulesOf(meta, f.module);
    const noModules = meta.modules.length === 0;

    // Defaults once reference data arrives; the version follows the server
    // until someone types their own.
    useEffect(() => {
        setF((prev) => {
            const server = prev.server_code || (meta.servers[0] || {}).code || "";
            const module = prev.module || (meta.modules[0] || {}).name || "";
            const subList = subModulesOf(meta, module);
            return {
                ...prev,
                server_code: server,
                module,
                sub_module: subList.includes(prev.sub_module) ? prev.sub_module : "",
                app_version: versionTouched.current ? prev.app_version : (meta.server_versions || {})[server] || prev.app_version,
            };
        });
    }, [meta]);

    const changeServer = (code) => {
        setF((prev) => {
            const list = orgsFor(meta, code);
            return {
                ...prev,
                server_code: code,
                org_name: list.includes(prev.org_name) ? prev.org_name : "",
                app_version: versionTouched.current ? prev.app_version : (meta.server_versions || {})[code] || "",
            };
        });
    };

    // Source search, debounced.
    useEffect(() => {
        if (!searchable || linked) { setResults([]); return undefined; }
        setSearching(true);
        const t = setTimeout(() => {
            qualityApi.searchSources(f.source, q.trim())
                .then((res) => setResults(res.data || []))
                .catch(() => setResults([]))
                .finally(() => setSearching(false));
        }, 300);
        return () => clearTimeout(t);
    }, [q, f.source, searchable, linked]);

    const pickSource = (r) => {
        const moduleKnown = meta.modules.some((m) => m.name === r.module);
        if (r.app_version) versionTouched.current = true;
        setF((prev) => ({
            ...prev,
            source_ref: r.ref,
            title: prev.title || r.title || "",
            server_code: r.server_code || prev.server_code,
            org_name: r.org_name || prev.org_name,
            module: moduleKnown ? r.module : prev.module,
            severity: r.severity || prev.severity,
            details: prev.details || r.details || "",
            hospital_reporter: prev.hospital_reporter || r.hospital_reporter || "",
            occurred_at: r.occurred_at ? toLocalInput(r.occurred_at) : prev.occurred_at,
            app_version: r.app_version || prev.app_version,
            browser_device: prev.browser_device || r.browser_device || "",
            screen_url: prev.screen_url || r.screen_url || "",
        }));
        setLinked(r);
        setQ("");
    };

    const unlink = () => { setLinked(null); set({ source_ref: "" }); };

    const addFiles = (list) => {
        const picked = Array.from(list || []);
        const big = picked.filter((x) => x.size > MAX_FILE_MB * 1024 * 1024).map((x) => x.name);
        if (big.length) showErrorToast(`Over ${MAX_FILE_MB} MB, not added: ${big.join(", ")}`);
        setFiles((prev) => [...prev, ...picked.filter((x) => x.size <= MAX_FILE_MB * 1024 * 1024)]);
    };

    // Same checks as the backend, so every problem shows at once.
    const validate = () => {
        const e = [];
        if (!f.title.trim()) e.push("Add a summary.");
        if (!f.module) e.push("Choose the module.");
        else if (subs.length && !f.sub_module) e.push("Choose the screen / sub-module.");
        if (!f.expected_result.trim()) e.push("Say what should have happened (expected result).");
        if (!f.actual_result.trim()) e.push("Say what actually happened (actual result).");
        if (f.occurred_at && new Date(f.occurred_at).getTime() > Date.now() + 5 * 60000) {
            e.push("When it happened cannot be in the future.");
        }
        return e;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        const e = validate();
        setErrors(e);
        if (e.length) return;
        setSaving(true);
        try {
            const payload = {
                ...f,
                title: f.title.trim(),
                occurred_at: f.occurred_at ? new Date(f.occurred_at).toISOString() : null,
            };
            const res = await qualityApi.createBug(payload);
            let bug = res.data;
            if (files.length) {
                try {
                    const up = await qualityApi.uploadAttachments(bug.id, files);
                    bug = up.data;
                } catch (err) {
                    showErrorToast(`Bug logged, but the files did not upload: ${err.message}. Add them from the bug's Details tab.`);
                }
            }
            showSuccessToast(`${res.message || `${bug.code} logged.`} Pick the release that introduced it.`);
            onCreated && onCreated(bug);
        } catch (err) {
            setErrors(err.payload?.errors?.length ? err.payload.errors : [err.message || "Could not log the bug."]);
        } finally {
            setSaving(false);
        }
    };

    const sevNote = useMemo(
        () => (f.patient_safety && (f.severity === "P2" || f.severity === "P3")
            ? "Patient safety is ticked, so this will be logged as P1." : ""),
        [f.patient_safety, f.severity]
    );

    return (
        <Modal
            wide
            title="Log production bug"
            onClose={onClose}
            footer={
                <>
                    <span className="qa-hint">Fields marked * are required.</span>
                    <span className="qa-sp" />
                    <button type="button" className="qa-btn" onClick={onClose}>Cancel</button>
                    <button type="submit" form="qa-raise-form" className="qa-btn qa-btn--primary" disabled={saving}>
                        {saving ? "Logging…" : "Log bug"}
                    </button>
                </>
            }
        >
            <form id="qa-raise-form" onSubmit={submit} noValidate>
                {errors.length > 0 && (
                    <div className="qa-banner qa-banner--bad">
                        <span className="qa-banner__tx">
                            <b>Fix these before logging the bug:</b>
                            {errors.map((x) => <div key={x}>{x}</div>)}
                        </span>
                    </div>
                )}

                {/* ── What happened ── */}
                <div className="qa-fsec">
                    <div className="qa-sec"><h4>What happened</h4></div>
                    <div className="qa-form">
                        <label className="qa-fl">
                            Source
                            <select
                                id="qa-raise-source" className="qa-input" value={f.source}
                                onChange={(e) => { set({ source: e.target.value, source_ref: "" }); setLinked(null); }}
                            >
                                {Object.entries(SOURCES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                        </label>

                        {searchable ? (
                            <div className="qa-fl">
                                {f.source === "customer_support" ? "Support ticket" : "Error"}
                                {linked ? (
                                    <div className="qa-linked">
                                        <SourceChip value={f.source} />
                                        <span className="qa-mono">{linked.ref}</span>
                                        <span className="qa-linked__t">{linked.title}</span>
                                        <button type="button" className="qa-x" onClick={unlink} aria-label="Remove link"><FiX size={14} /></button>
                                    </div>
                                ) : (
                                    <div className="qa-search-pick">
                                        <FiSearch size={13} className="qa-search-pick__i" />
                                        <input
                                            id="qa-raise-find" className="qa-input" value={q} onChange={(e) => setQ(e.target.value)}
                                            placeholder={f.source === "customer_support" ? "Search code, subject or hospital" : "Search URL, hospital, module or error #"}
                                        />
                                        <div className="qa-search-pick__list">
                                            {searching && <div className="qa-hint" style={{ padding: 8 }}>Searching…</div>}
                                            {!searching && results.length === 0 && (
                                                <div className="qa-hint" style={{ padding: 8 }}>No matches. You can still log it without a link.</div>
                                            )}
                                            {results.map((r) => (
                                                <button type="button" key={r.ref} onClick={() => pickSource(r)}>
                                                    <span className="qa-mono">{r.ref}</span> {r.title}
                                                    <span className="qa-hint"> · {r.org_name}{r.occurrences ? ` · ${r.occurrences} occurrences` : ""}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <label className="qa-fl">
                                Reference
                                <input
                                    id="qa-raise-ref" className="qa-input" value={f.source_ref}
                                    onChange={(e) => set({ source_ref: e.target.value })} placeholder="e.g. QA finding, audit alert name"
                                />
                            </label>
                        )}

                        <label className="qa-fl qa-full">
                            <span className="qa-req">Summary</span>
                            <input
                                id="qa-raise-title" className="qa-input" value={f.title} onChange={(e) => set({ title: e.target.value })}
                                placeholder="e.g. Pending amount wrong after a partial sales return"
                            />
                        </label>
                        <label className="qa-fl">
                            <span className="qa-req">Expected result</span>
                            <textarea
                                id="qa-raise-expected" className="qa-input" value={f.expected_result}
                                onChange={(e) => set({ expected_result: e.target.value })} placeholder="What should have happened"
                            />
                        </label>
                        <label className="qa-fl">
                            <span className="qa-req">Actual result</span>
                            <textarea
                                id="qa-raise-actual" className="qa-input" value={f.actual_result}
                                onChange={(e) => set({ actual_result: e.target.value })} placeholder="What happened instead"
                            />
                        </label>
                        <label className="qa-fl qa-full">
                            Steps to reproduce
                            <textarea
                                id="qa-raise-steps" className="qa-input" value={f.steps_to_reproduce}
                                onChange={(e) => set({ steps_to_reproduce: e.target.value })}
                                placeholder={"1. Open Pharmacy → Billing\n2. Return 2 of 5 items\n3. Check Pending amount"}
                            />
                        </label>
                        <label className="qa-fl">
                            <span className="qa-req">Severity</span>
                            <select id="qa-raise-sev" className="qa-input" value={f.severity} onChange={(e) => set({ severity: e.target.value })}>
                                {Object.entries(SEVERITY).map(([k, v]) => <option key={k} value={k}>{k} · {v}</option>)}
                            </select>
                            {sevNote && <span className="qa-typehint" style={{ color: "var(--qa-orange)" }}>{sevNote}</span>}
                        </label>
                        <label className="qa-fl">
                            <span className="qa-req">When it happened</span>
                            <input
                                id="qa-raise-when" type="datetime-local" className="qa-input" value={f.occurred_at}
                                max={toLocalInput()} onChange={(e) => set({ occurred_at: e.target.value })}
                            />
                            <span className="qa-typehint">Used to find the release that caused it. Leave as now if unsure.</span>
                        </label>
                        <label className="qa-fl">
                            Reported by (at the hospital)
                            <input
                                id="qa-raise-reporter" className="qa-input" value={f.hospital_reporter}
                                onChange={(e) => set({ hospital_reporter: e.target.value })} placeholder="Name or role, e.g. Pharmacy in-charge"
                            />
                        </label>
                        <label className="qa-fl">
                            Other details
                            <input
                                id="qa-raise-details" className="qa-input" value={f.details}
                                onChange={(e) => set({ details: e.target.value })} placeholder="Anything else worth knowing"
                            />
                        </label>
                    </div>
                </div>

                {/* ── Where ── */}
                <div className="qa-fsec">
                    <div className="qa-sec"><h4>Where</h4></div>
                    <div className="qa-form">
                        <label className="qa-fl">
                            Server
                            <select id="qa-raise-server" className="qa-input" value={f.server_code} onChange={(e) => changeServer(e.target.value)}>
                                {meta.servers.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
                            </select>
                        </label>
                        <label className="qa-fl">
                            Hospital
                            <select id="qa-raise-org" className="qa-input" value={f.org_name} onChange={(e) => set({ org_name: e.target.value })}>
                                <option value="">Choose…</option>
                                {orgs.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </label>
                        <label className="qa-fl">
                            <span className="qa-req">Module</span>
                            {noModules ? (
                                <div className="qa-banner qa-banner--warn" style={{ margin: 0 }}>
                                    <span className="qa-banner__tx">
                                        No modules yet. Add them in <Link to="/MastersTab?tab=Modules">Masters → Modules</Link>.
                                    </span>
                                </div>
                            ) : (
                                <select
                                    id="qa-raise-module" className="qa-input" value={f.module}
                                    onChange={(e) => set({ module: e.target.value, sub_module: "" })}
                                >
                                    {meta.modules.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
                                </select>
                            )}
                        </label>
                        <label className="qa-fl">
                            <span className={subs.length ? "qa-req" : ""}>Screen / sub-module</span>
                            {subs.length ? (
                                <select id="qa-raise-sub" className="qa-input" value={f.sub_module} onChange={(e) => set({ sub_module: e.target.value })}>
                                    <option value="">Choose…</option>
                                    {subs.map((x) => <option key={x} value={x}>{x}</option>)}
                                </select>
                            ) : (
                                <span className="qa-typehint" style={{ paddingTop: 10 }}>
                                    No sub-modules for this module. <Link to="/MastersTab?tab=Sub-Modules">Add them in Masters</Link>.
                                </span>
                            )}
                        </label>
                        <label className="qa-fl">
                            App version on that server
                            <input
                                id="qa-raise-version" className="qa-input" value={f.app_version}
                                onChange={(e) => { versionTouched.current = true; set({ app_version: e.target.value }); }}
                                placeholder="e.g. v-4.18.5"
                            />
                        </label>
                        <label className="qa-fl">
                            Screen URL
                            <input
                                id="qa-raise-url" className="qa-input" value={f.screen_url}
                                onChange={(e) => set({ screen_url: e.target.value })} placeholder="e.g. /pharmacy/billing"
                            />
                        </label>
                        <label className="qa-fl">
                            Browser / device
                            <input
                                id="qa-raise-browser" className="qa-input" value={f.browser_device}
                                onChange={(e) => set({ browser_device: e.target.value })} placeholder="e.g. Chrome on Windows, tablet"
                            />
                        </label>
                    </div>
                </div>

                {/* ── Impact ── */}
                <div className="qa-fsec">
                    <div className="qa-sec"><h4>Impact</h4></div>
                    <div className="qa-form">
                        <label className="qa-fl">
                            Users affected
                            <input
                                id="qa-raise-users" type="number" min="0" className="qa-input" value={f.users_affected}
                                onChange={(e) => set({ users_affected: e.target.value })} placeholder="Best estimate"
                            />
                        </label>
                        <label className="qa-fl">
                            Hospitals affected
                            <input
                                id="qa-raise-hospitals" type="number" min="0" className="qa-input" value={f.hospitals_affected}
                                onChange={(e) => set({ hospitals_affected: e.target.value })} placeholder="Best estimate"
                            />
                        </label>
                        <label className="qa-check qa-full">
                            <input
                                id="qa-raise-safety" type="checkbox" checked={f.patient_safety}
                                onChange={(e) => set({ patient_safety: e.target.checked })}
                            />
                            <span>
                                <b>Patient safety or data integrity affected</b>
                                <span className="qa-typehint"> e.g. wrong dose, wrong patient, wrong amount billed, lost records. Severity becomes at least P1.</span>
                            </span>
                        </label>
                    </div>
                </div>

                {/* ── Evidence ── */}
                <div className="qa-fsec">
                    <div className="qa-sec"><h4>Evidence <span className="qa-hint">Screenshots, logs or error traces, up to {MAX_FILE_MB} MB each</span></h4></div>
                    <label className="qa-drop">
                        <input id="qa-raise-files" type="file" multiple onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                        <FiPaperclip size={14} /> Choose files
                    </label>
                    {files.length > 0 && (
                        <ul className="qa-files">
                            {files.map((x, i) => (
                                <li key={`${x.name}-${i}`}>
                                    <span>{x.name}</span>
                                    <span className="qa-hint">{(x.size / 1024).toFixed(0)} KB</span>
                                    <button type="button" className="qa-x" onClick={() => setFiles((prev) => prev.filter((_, k) => k !== i))}
                                        aria-label={`Remove ${x.name}`}><FiX size={14} /></button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </form>
        </Modal>
    );
}
